import { Check, Copy } from "lucide-react";
import qrcode from "qrcode-generator";
import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { CodeField } from "./CodeField.tsx";

/** The `otpauth://` URI as a QR code, drawn as SVG cells (no remote image, no inline HTML). */
export function QrCode({ value, label }: { value: string; label: string }) {
  const cells = useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(value);
    qr.make();
    const count = qr.getModuleCount();
    const dark: string[] = [];
    for (let row = 0; row < count; row++) {
      for (let col = 0; col < count; col++) {
        if (qr.isDark(row, col)) {
          dark.push(`M${col + 4} ${row + 4}h1v1h-1z`);
        }
      }
    }
    return { size: count + 8, path: dark.join("") };
  }, [value]);
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${cells.size} ${cells.size}`}
      className="size-36 shrink-0 self-center rounded-md sm:self-start"
      shapeRendering="crispEdges"
    >
      {/* Dark on light in both themes, with its quiet zone: what scanners read. */}
      <rect width={cells.size} height={cells.size} fill="#fff" />
      <path d={cells.path} fill="#000" />
    </svg>
  );
}

/** The setup key in groups of four, easier to type into an authenticator app. */
const grouped = (secret: string) => secret.replace(/(.{4})/g, "$1 ").trim();

/**
 * Authenticator-app enrolment: the QR code, the key as text (revealed on demand, copyable)
 * and the first code to confirm it. Fully keyboard operable; the outcome is announced by the
 * caller.
 */
export function TotpSetup(props: {
  secret: string;
  uri: string;
  pending: boolean;
  error: string | null;
  onConfirm: (code: string) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [code, setCode] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(props.secret);
      setCopied(true);
    } catch {
      setShowKey(true);
    }
  }

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        props.onConfirm(code);
      }}
    >
      <div className="flex flex-col gap-4 rounded-lg border border-line bg-surface-raised p-4 sm:flex-row sm:items-start">
        <QrCode value={props.uri} label={t("totp.qrLabel")} />
        <div className="flex min-w-0 flex-col gap-3">
          <ol className="flex flex-col gap-2 text-sm leading-relaxed text-fg-secondary">
            {[t("totp.step1"), t("totp.step2")].map((text, index) => (
              <li key={text} className="flex gap-2.5">
                <span
                  aria-hidden="true"
                  className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-surface-sunken text-xs font-medium text-fg-secondary tabular-nums"
                >
                  {index + 1}
                </span>
                <span>
                  <span className="sr-only">{index + 1}. </span>
                  {text}
                </span>
              </li>
            ))}
          </ol>
          {showKey ? (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-fg-muted" id={`${id}-key`}>
                {t("totp.key")}
              </span>
              <div className="flex items-center gap-1 rounded-md bg-surface-sunken py-0.5 pr-0.5 pl-2">
                <code className="min-w-0 flex-1 font-mono text-[13px] break-all text-fg select-all">
                  {grouped(props.secret)}
                </code>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={copied ? t("totp.copied") : t("totp.copy")}
                  title={copied ? t("totp.copied") : t("totp.copy")}
                  onClick={() => void copy()}
                >
                  {copied ? (
                    <Check aria-hidden="true" strokeWidth={1.75} />
                  ) : (
                    <Copy aria-hidden="true" strokeWidth={1.75} />
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto self-start p-0 text-accent-text"
              onClick={() => setShowKey(true)}
            >
              {t("totp.showKey")}
            </Button>
          )}
        </div>
      </div>
      <CodeField
        id={`${id}-code`}
        value={code}
        onChange={setCode}
        invalid={!!props.error}
        disabled={props.pending}
        autoFocus={false}
      />
      {props.error ? <InlineError>{props.error}</InlineError> : null}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button type="submit" size="lg" className="sm:flex-1" disabled={props.pending}>
          {props.pending ? t("totp.confirming") : t("totp.confirm")}
        </Button>
        {props.onCancel ? (
          <Button type="button" variant="outline" size="lg" onClick={props.onCancel}>
            {t("app.cancel")}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
